import clsx from 'clsx';
import moment, { Moment } from 'moment';
import 'react-dates/constants';
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import { parseISO, isValid } from 'date-fns';
import { SingleDatePicker } from 'react-dates';
import { BloodSample, BloodSamplesAjaxResult } from '../PatientBloodSamples';
import { useCallback, useContext, useEffect, useState } from 'react';
import { Check } from '../../../../../shared/assets/check';
import { Pencil } from '../../../../../shared/assets/pencil';
import { XClose } from '../../../../../shared/assets/x-close';
import { fetchAuthenticated } from '../../../../../controllers';
import { Button } from '../../../../../components/Button/Button';
import { Row } from '../../../../../components/common/table/Row/Row';
import { TAS_BACK_END_API_URL, URL_TAS_VERSION } from '../../../../../constants';
import { Cell } from '../../../../../components/common/table/Cell/Cell';
import { FlexContainer } from '../../../../../components/FlexContainer/FlexContainer';
import { convertDate, convertTime } from '../../../../../components/common/convertDateTime/convertDateTime';
import './react_dates_overrides.scss';
import { LoadingSpinner, smallSpinner } from '../../../../../components/common/loader/LoadingSpinner';
import './SampleHistoryRow.scss';
import { neutral300, neutral50, primaryBlue400, warning } from '../../../../../shared/styles/variables';
import { TextInput } from '../../../../../components/TextInput/TextInput';
import '../../../../../components/TextInput/TextInput.scss';
import { decryptObject, encryptObject } from '../../../../../components/Encryption/obfuscationHandler';
import { v4 } from 'uuid';
import { PopupMessageContext } from 'src/shared/contexts/PopupMessageContext';
import TimeDropdown from 'src/components/common/timeSelector/timeSelector';

export interface SampleHistoryRowProps {
  sample: BloodSample;
  index: number;
  patientId?: string;
  updateApiData: any;
  token: string | null;
}

export const SampleHistoryRow = ({ sample, index, patientId, updateApiData, token }: SampleHistoryRowProps) => {
  const [edit, setEdit] = useState(false);
  const [status, setStatus] = useState('');
  const [tempDrawDate, setTempDrawDate] = useState<any>(null);
  const [drawDate, setDrawDate] = useState<string>(convertDate(sample.body.draw_date_time_utc));
  const [tempDrawTime, setTempDrawTime] = useState<any>(null);
  const [drawTime, setDrawTime] = useState<string>(convertTime(sample.body.draw_date_time_utc));
  const [sampleId, setSampleId] = useState<string>(sample.body.encrypt_vial_id);
  const [focused, setFocused] = useState<boolean>(false);
  const [enableButton, setEnableButton] = useState<boolean>(true);
  const { addPopupMessage } = useContext(PopupMessageContext);
  const [toggleTimeDropDown, setToggleTimeDropDown] = useState<boolean>(false);

  const getISOTime = useCallback(() => {
    const [sampleDay, sampleMonth, sampleYear] = convertDate(sample.body.draw_date_time_utc).split('-');
    const [sampleHour, sampleMinutes, sampleSeconds] = convertTime(sample.body.draw_date_time_utc).split(':');
    const year = tempDrawDate ? tempDrawDate.get('year') : sampleYear;
    const month = tempDrawDate ? tempDrawDate.get('month') : Number('JanFebMarAprMayJunJulAugSepOctNovDec'.indexOf(sampleMonth) / 3);
    const days = tempDrawDate ? tempDrawDate.get('date') : Number(sampleDay);
    const hours = tempDrawTime ? Number(tempDrawTime.slice(0, 2)) : Number(sampleHour);
    const minutes = tempDrawTime ? Number(tempDrawTime.slice(3, 5)) : Number(sampleMinutes);
    const seconds = tempDrawTime ? Number(tempDrawTime.slice(6)) : Number(sampleSeconds);

    const time = new Date(year, month, days, hours, minutes, seconds);
    let timeISO;
    try {
      timeISO = time.toISOString();
    } catch (e) {
      timeISO = 'ERROR';
    }

    return timeISO;
  }, [tempDrawDate, tempDrawTime, sample.body.draw_date_time_utc]);

  useEffect(() => {
    if (tempDrawTime && (tempDrawTime.length !== 8 || tempDrawTime.split(':').length !== 3)) return setEnableButton(false);
    setEnableButton(validTime(getISOTime()));
  }, [tempDrawDate, tempDrawTime, getISOTime]);

  const validTime = (timeToCheck: string) => {
    const unixTimeToCheck = new Date(timeToCheck).getTime();

    const currentUnixTime = new Date().getTime();

    return unixTimeToCheck < currentUnixTime ? true : false;
  };

  const handleBloodSampleUpdate = async () => {
    if (!tempDrawDate && !tempDrawTime) return setStatus('');
    setStatus('Uploading Data');

    const url = TAS_BACK_END_API_URL + `${URL_TAS_VERSION}/patients/${patientId}/blood-samples`;
    let utcFormatedDrawDateTime = !tempDrawDate && !tempDrawTime ? sample.body.draw_date_time_utc : getISOTime();

    if (!isValid(parseISO(utcFormatedDrawDateTime)) || !validTime(utcFormatedDrawDateTime)) return setStatus('Date/Time Error');

    setSampleId(sample.body.encrypt_vial_id);
    setDrawDate(convertDate(utcFormatedDrawDateTime));
    setDrawTime(convertTime(utcFormatedDrawDateTime));

    const body = {
      version: sample?.header?.version,
      encrypt_vial_id: sample.body.encrypt_vial_id,
      item_id: sample.header.item_id,
      draw_date_time_utc: utcFormatedDrawDateTime,
    };

    const config: RequestInit = {
      method: 'PATCH',
      body: JSON.stringify(await encryptObject(body, token, null)),
      headers: { Authorization: `Bearer ${token}` },
    };

    try {
      const response = await fetchAuthenticated<BloodSamplesAjaxResult>(url, config);

      if (response?.ok) {
        const data = await decryptObject(response.parsedBody?.result, token, null);
        setStatus('Added!');
        updateApiData(data?.items);
      }
    } catch (error) {
      addPopupMessage({
        id: v4(),
        message: 'There was an error changing the blood sample. Please retry request.',
        header: 'Change',
      });
      if (error) setStatus('Error in Upload');
    }
  };

  const updateTempDrawTime = (timeInfo: any) => {
    setTempDrawTime(`${timeInfo.hour}:${timeInfo.minute}:${timeInfo.second}`);
  };

  const toggleDropDowns = (dropDown: 'time' | 'date') => {
    if ('date' === dropDown) {
      setFocused(!focused);
      setToggleTimeDropDown(false);
    } else {
      setToggleTimeDropDown(!toggleTimeDropDown);
      setFocused(false);
    }
  };

  const dayAdjustment = new Date().getUTCHours() - new Date().getHours() + 1;
  return (
    <Row index={index} key={sample._id} style={{ paddingRight: '24px' }} classes={'sample-history-row-container'}>
      <Cell>{sampleId}</Cell>
      <Cell style={{ textIndent: '5em' }}>{sample?.body?.icp_oes_boron_concentration_ppm || 'N/A'}</Cell>
      <Cell>
        {status === 'Editing' ? (
          <SingleDatePicker
            placeholder={drawDate}
            date={tempDrawDate}
            onDateChange={(tempDrawDate: Moment | null) => {
              setTempDrawDate(tempDrawDate);
              setFocused(!focused);
            }}
            appendToBody={true}
            focused={focused}
            onFocusChange={() => toggleDropDowns('date')}
            id="singleDatePicker"
            numberOfMonths={1}
            displayFormat="DD-MMM-yyyy"
            isOutsideRange={(day: Moment) => day.isAfter(moment().add(dayAdjustment, 'hours'))}
            noBorder
            small
          />
        ) : (
          drawDate
        )}
      </Cell>
      <Cell style={{ position: 'relative' }}>
        {status === 'Editing' ? (
          <>
            <TextInput
              value={tempDrawTime || drawTime}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTempDrawTime(e.target.value)}
              width={95}
              borderColor={!enableButton ? warning : ''}
              borderWidth={!enableButton ? '1.5px' : ''}
              onClick={() => toggleDropDowns('time')}
            />
            <TimeDropdown
              columnWidth={60}
              isOpen={toggleTimeDropDown}
              onTimeSelect={(details: any) => updateTempDrawTime(details)}
              xTranslation={-30}
              startTime={tempDrawTime || drawTime}
            />
          </>
        ) : (
          drawTime
        )}
      </Cell>
      <Cell>{sample?.header?.user?.name ? sample?.header?.user?.name : sample?.header?.user?.id ? sample?.header?.user?.id : 'Unknown'}</Cell>
      <Cell>
        <FlexContainer justifyContent="center">
          <>
            {edit && (
              <div className="actionButtons">
                <div className="cancel-button-container">
                  <Button
                    icon={<XClose fill={primaryBlue400} />}
                    onClick={() => {
                      setEdit(false);
                      setStatus('');
                    }}
                    width={'40px'}
                    height={'40px'}
                    className={clsx('square-icon-button', 'cancel-button')}
                  ></Button>
                </div>
                <Button
                  icon={<Check fill={neutral50} />}
                  onClick={() => {
                    setEdit(false);
                    handleBloodSampleUpdate();
                  }}
                  width={'40px'}
                  height={'40px'}
                  className={clsx('square-icon-button', 'save-button')}
                  background-color={!enableButton ? neutral300 : null}
                  disabled={!enableButton}
                ></Button>
              </div>
            )}
          </>
          <div>
            {status === 'Uploading Data' ? (
              <LoadingSpinner width={smallSpinner} height={smallSpinner} />
            ) : (
              !edit && (
                <Button
                  icon={<Pencil fill={primaryBlue400} />}
                  onClick={() => {
                    setEdit(true);
                    setStatus('Editing');
                  }}
                  width={'40px'}
                  height={'40px'}
                  className={clsx('square-icon-button', 'edit-button-enabled')}
                ></Button>
              )
            )}
          </div>
        </FlexContainer>
      </Cell>
      <Cell></Cell>
    </Row>
  );
};
